import {
  ReactNode,
  Ref,
  RefAttributes,
  RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type JSX,
} from "react";

import Tree from "ds/components/Tree";
import { convertToTreeStructure, isNodeVisible, search, updateTreeRendering } from "utils/tree";

import { TreeListContext } from "../Context";
import TreeListItemWithCheckbox from "../ItemWithCheckbox";
import TreeListNodeMultiSelect from "./Node";
import { TreeListApi, TreeListItemWithCheckboxProps } from "../types";
import useKeyboardNavigation from "../useKeyboardNavigation";
import styles from "../styles.module.css";

type TreeListMultiSelectProps<T> = {
  id?: string;
  ariaLabel?: string;
  data: T[];
  idKey: keyof T;
  parentKey: keyof T;
  nameKey: keyof T;
  selectedKeys?: Set<string>;
  onSelectionChange?: (selectedKeys: Set<string>) => void;
  onEscape?: () => void;
  renderItem?: (item: TreeListItemWithCheckboxProps<T>) => ReactNode;
  renderEmptyItem?: () => ReactNode;
  searchQuery?: string;
  selectedRef?: RefObject<HTMLDivElement | null>;
  hiddenKeys?: string[];
  disabledKeys?: string[];
  maxDepth?: number;
  disableIndeterminate?: boolean;
  defaultSelection?: string;
};

const defaultRenderItem = <T extends object>(props: TreeListItemWithCheckboxProps<T>) => (
  <TreeListItemWithCheckbox {...props} />
);

// eslint-disable-next-line spacelift/display-name
const TreeListMultiSelect = <T extends object>(
  {
    id,
    ariaLabel,
    selectedKeys = new Set(),
    searchQuery,
    data,
    idKey,
    parentKey,
    nameKey,
    onSelectionChange,
    onEscape,
    renderItem = defaultRenderItem,
    renderEmptyItem,
    selectedRef,
    hiddenKeys,
    disabledKeys,
    maxDepth = Infinity,
    disableIndeterminate = false,
    defaultSelection,
  }: TreeListMultiSelectProps<T>,
  ref: Ref<TreeListApi>
) => {
  const { tree, flatList } = useMemo(
    () => convertToTreeStructure<T>(data, idKey, parentKey, nameKey),
    [data, idKey, nameKey, parentKey]
  );

  // Merge selectedKeys with defaultSelection
  const effectiveSelectedKeys = useMemo(() => {
    const merged = new Set(selectedKeys);
    if (defaultSelection) {
      merged.add(defaultSelection);
    }
    return merged;
  }, [selectedKeys, defaultSelection]);

  // Merge disabledKeys with defaultSelection
  const effectiveDisabledKeys = useMemo(() => {
    const merged = [...(disabledKeys || [])];
    if (defaultSelection && !merged.includes(defaultSelection)) {
      merged.push(defaultSelection);
    }
    return merged;
  }, [disabledKeys, defaultSelection]);

  const [focusedKey, setFocusedKey] = useState<string | undefined>();

  const allKeysSet = useMemo(() => {
    const allKeys = flatList.map((item) => item.id);
    return new Set(allKeys);
  }, [flatList]);

  const [expandedKeys, setExpandedKeys] = useState(allKeysSet);
  const [renderedKeys, setRenderedKeys] = useState(allKeysSet);

  useEffect(() => {
    if (searchQuery) {
      const matchedPaths = search(flatList, searchQuery);

      const { expandedNodes, renderedNodes } = updateTreeRendering(flatList, matchedPaths);

      setExpandedKeys(expandedNodes);
      setRenderedKeys(renderedNodes);
    } else {
      setExpandedKeys(allKeysSet);
      setRenderedKeys(allKeysSet);
    }
  }, [allKeysSet, flatList, searchQuery]);

  const finalRenderedKeys = useMemo(() => {
    const keysToRender = new Set(renderedKeys);
    if (hiddenKeys) {
      hiddenKeys.forEach((id) => {
        keysToRender.delete(id);
      });
    }

    return keysToRender;
  }, [hiddenKeys, renderedKeys]);

  const toggleKey = useCallback(
    (key: string) => {
      setExpandedKeys((prev) => {
        const newKeys = new Set(prev);
        if (newKeys.has(key)) {
          newKeys.delete(key);
        } else {
          newKeys.add(key);
        }
        return newKeys;
      });
    },
    []
  );

  const handleCheckboxChange = useCallback(
    (id: string, checked: boolean) => {
      if (!onSelectionChange) return;

      // Don't allow changing defaultSelection item
      if (defaultSelection === id) return;

      const newSelectedKeys = new Set(selectedKeys);
      if (checked) {
        newSelectedKeys.add(id);
      } else {
        newSelectedKeys.delete(id);
      }
      onSelectionChange(newSelectedKeys);
    },
    [onSelectionChange, selectedKeys, defaultSelection]
  );

  const getIndeterminateState = useCallback(
    (nodeId: string): boolean => {
      if (disableIndeterminate) return false;

      const node = flatList.find((n) => n.id === nodeId);
      if (!node || !node.children.length) return false;

      const allDescendants = new Set<string>();
      const collectDescendants = (id: string) => {
        const current = flatList.find((n) => n.id === id);
        if (current) {
          current.children.forEach((child) => {
            allDescendants.add(child.id);
            collectDescendants(child.id);
          });
        }
      };
      collectDescendants(nodeId);

      if (allDescendants.size === 0) return false;

      const selectedDescendants = Array.from(allDescendants).filter((id) =>
        effectiveSelectedKeys.has(id)
      ).length;

      return selectedDescendants > 0 && selectedDescendants < allDescendants.size;
    },
    [disableIndeterminate, flatList, effectiveSelectedKeys]
  );

  const visibleNodes = useMemo(
    () => flatList.filter((node) => isNodeVisible(node, finalRenderedKeys, expandedKeys)),
    [expandedKeys, finalRenderedKeys, flatList]
  );

  const treeDepth = useMemo(
    () => Math.max(...visibleNodes.map((node) => node.path.length)),
    [visibleNodes]
  );

  const handleKeyDown = useKeyboardNavigation({
    disabledKeys: effectiveDisabledKeys,
    expandedKeys,
    visibleNodes,
    onSelect: (id: string) => handleCheckboxChange(id, !effectiveSelectedKeys.has(id)),
    onFocus: setFocusedKey,
    onEscape,
    toggleKey,
  });

  const contextValue = useMemo(
    () => ({
      selectedKey: undefined,
      expandedKeys,
      searchQuery,
      renderedKeys: finalRenderedKeys,
      onChange: undefined,
      toggleKey,
      selectedRef,
      disabledKeys: effectiveDisabledKeys,
      onFocus: setFocusedKey,
      focusedKey,
      onKeyDown: handleKeyDown,
    }),
    [
      expandedKeys,
      searchQuery,
      finalRenderedKeys,
      selectedRef,
      effectiveDisabledKeys,
      focusedKey,
      handleKeyDown,
      toggleKey,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      focus: () => setFocusedKey(visibleNodes[0]?.id),
    }),
    [visibleNodes]
  );

  return (
    <TreeListContext.Provider value={contextValue}>
      <Tree
        aria-label={ariaLabel}
        id={id}
        compact
        gap="small"
        role="tree"
        aria-multiselectable
        data-maxdepth={treeDepth > maxDepth ? treeDepth : undefined}
        className={styles.rootTree}
      >
        {tree.map((node) => (
          <TreeListNodeMultiSelect<T>
            key={node.id}
            node={node}
            renderItem={renderItem}
            isRoot
            selectedKeys={effectiveSelectedKeys}
            onCheckboxChange={handleCheckboxChange}
            getIndeterminateState={getIndeterminateState}
            defaultSelection={defaultSelection}
          />
        ))}

        {finalRenderedKeys.size === 0 && renderEmptyItem?.()}
      </Tree>
    </TreeListContext.Provider>
  );
};

interface TreeListMultiSelectComponent {
  <T extends object>(props: TreeListMultiSelectProps<T> & RefAttributes<TreeListApi>): JSX.Element;
  displayName?: string;
}

const TreeListMultiSelectWithRef = forwardRef(
  TreeListMultiSelect
) as unknown as TreeListMultiSelectComponent;
TreeListMultiSelectWithRef.displayName = "DS.TreeList.MultiSelect";

export default TreeListMultiSelectWithRef;
